import {ChevronDownIcon, ChevronUpIcon} from '@heroicons/react/solid';
import {useState} from 'react';

import {
  AfriexAdminPermission,
  AfriexTransactionChannel,
  PaymentMethodNameResolutionCacheState,
  PaymentMethodNameResolutionStatus,
} from '@afriex/core/domain/enums';

import {Badge, Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import ConfirmationModal from '@/components/Modals/ConfirmationModal';
import {DDMonYYYY} from '@/utils/date';

import type {NameResolution} from './types';

// Th/Td carry no horizontal padding of their own; each table sets it.
const FIRST_CELL = 'pl-6 pr-4';
const CELL = 'px-4';
const EXPANDED_TINT = 'bg-gray-50 dark:bg-gray-900';
const COLUMN_COUNT = 8;

const CACHE_STATE_BADGES: Record<
  PaymentMethodNameResolutionCacheState,
  {label: string; colorScheme: string; hint: string}
> = {
  [PaymentMethodNameResolutionCacheState.SERVED]: {
    label: 'Served',
    colorScheme: 'successful',
    hint: 'Replayed instead of calling the resolver.',
  },
  [PaymentMethodNameResolutionCacheState.STALE]: {
    label: 'Stale',
    colorScheme: 'pending',
    hint: 'Older than 6 months, so the next lookup already goes to the resolver.',
  },
  [PaymentMethodNameResolutionCacheState.UNTRUSTED]: {
    label: 'Not trusted',
    colorScheme: 'pending',
    hint: 'Its resolver is not on the trusted list, so the next lookup already goes to the resolver.',
  },
  [PaymentMethodNameResolutionCacheState.EXPIRED]: {
    label: 'Invalidated',
    colorScheme: 'gray',
    hint: 'The next lookup goes to the resolver and caches the fresh result.',
  },
};

const institutionLabel = (row: NameResolution) =>
  row.channel === AfriexTransactionChannel.MOBILE_MONEY
    ? row.info.provider || row.institutionCode
    : row.info.bankName || row.institutionCode;

type NameResolutionTableProps = {
  items: NameResolution[];
  onInvalidate: (id: string) => Promise<void>;
};

const NameResolutionTable = ({
  items,
  onInvalidate,
}: NameResolutionTableProps) => {
  const [selected, setSelected] = useState<NameResolution | null>(null);
  const [isInvalidating, setIsInvalidating] = useState(false);

  const confirmInvalidate = async () => {
    if (!selected) {
      return;
    }
    setIsInvalidating(true);
    await onInvalidate(selected.id);
    setIsInvalidating(false);
    setSelected(null);
  };

  return (
    <>
      <Table className="overflow-hidden">
        <Thead>
          <Tr>
            <Th className={FIRST_CELL}>Account</Th>
            <Th className={CELL}>Name</Th>
            <Th className={CELL}>Bank / Provider</Th>
            <Th className={CELL}>Country</Th>
            <Th className={CELL}>Resolved by</Th>
            <Th className={CELL}>Last resolved</Th>
            <Th className={CELL}>State</Th>
            <Th className={CELL}>
              <span className="sr-only">Details</span>
            </Th>
          </Tr>
        </Thead>
        <TBody>
          {items.map((row, index) => (
            <NameResolutionRow
              key={row.id}
              row={row}
              index={index}
              onInvalidate={() => setSelected(row)}
            />
          ))}
        </TBody>
      </Table>

      {selected ? (
        <ConfirmationModal
          isOpen
          title="Invalidate cached lookup"
          label={`Invalidate the cached name for ${selected.accountIdentifier} (${institutionLabel(selected)})? The next lookup will call the currency's current resolver and cache the fresh result.`}
          confirmAction={confirmInvalidate}
          confirmActionLabel="Invalidate"
          cancelAction={() => setSelected(null)}
          cancelActionLabel="Cancel"
          loading={isInvalidating}
          loadingLabel="Invalidating..."
          width="w-96"
        />
      ) : null}
    </>
  );
};

const NameResolutionRow = ({
  row,
  index,
  onInvalidate,
}: {
  row: NameResolution;
  index: number;
  onInvalidate: () => void;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';
  // An open row and its detail panel share one tint, so they read as a unit.
  const rowTint = isExpanded ? EXPANDED_TINT : zebraRow;
  const badge = CACHE_STATE_BADGES[row.cacheState];
  const details: [string, string][] = [
    [
      'Channel',
      row.channel === AfriexTransactionChannel.MOBILE_MONEY
        ? 'Mobile money'
        : 'Bank',
    ],
    [
      row.channel === AfriexTransactionChannel.MOBILE_MONEY
        ? 'Provider'
        : 'Bank code',
      row.institutionCode,
    ],
    ['Processor', row.processor || '-'],
    ['Resolver calls', String(row.resolveCount)],
    ['First cached', row.createdAt ? DDMonYYYY(row.createdAt, true) : '-'],
    ['Last resolved', DDMonYYYY(row.lastResolvedAt, true)],
  ];

  return (
    <>
      <Tr className={rowTint}>
        <Td className={FIRST_CELL}>
          <span className="font-mono">{row.accountIdentifier}</span>
        </Td>
        <Td className={CELL}>{row.info.accountName || '-'}</Td>
        <Td className={CELL}>{institutionLabel(row)}</Td>
        <Td className={CELL}>{row.country}</Td>
        <Td className={CELL}>{row.resolvedBy}</Td>
        <Td className={CELL}>{DDMonYYYY(row.lastResolvedAt)}</Td>
        <Td className={CELL}>
          <span title={badge.hint}>
            <Badge label={badge.label} colorScheme={badge.colorScheme} />
          </span>
        </Td>
        <Td className={CELL}>
          <Button
            variant="unStyled"
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Hide' : 'Show'} details for ${row.accountIdentifier}`}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronUpIcon className="w-4 h-4" />
            ) : (
              <ChevronDownIcon className="w-4 h-4" />
            )}
          </Button>
        </Td>
      </Tr>
      {isExpanded ? (
        <Tr className={`${EXPANDED_TINT} border-t-0`} enableHover={false}>
          <Td
            colspan={COLUMN_COUNT}
            className={`${FIRST_CELL} pt-0 whitespace-normal`}
          >
            <div className="py-2 space-y-2">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <dl className="grid grid-cols-[auto_1fr] md:grid-cols-[auto_1fr_auto_1fr_auto_1fr] gap-x-3 gap-y-1 text-sm">
                  {details.map(([label, value]) => (
                    <div key={label} className="contents">
                      <dt className="text-gray-500 dark:text-gray-400">
                        {label}
                      </dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
                {row.status === PaymentMethodNameResolutionStatus.ACTIVE ? (
                  <PermissionsProvider
                    permission={
                      AfriexAdminPermission.ProcessorNameResolutionInvalidate
                    }
                  >
                    <Button variant="outline" onClick={onInvalidate}>
                      Invalidate
                    </Button>
                  </PermissionsProvider>
                ) : null}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {badge.hint}
              </p>
            </div>
          </Td>
        </Tr>
      ) : null}
    </>
  );
};

export default NameResolutionTable;
